import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Modal, Box, Typography, LinearProgress, CircularProgress } from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import { GenerationProgress } from '../../components/loading/LoadingStates';
import { ErrorBoundary } from '../../components/errors/ErrorDisplay';
import DocumentViewer from '../../components/viewers/DocumentViewer';
import {
  User,
  FormatType,
  ClientContext,
  ExportOptions
} from './types';
import ProfileEditor from './components/ProfileEditor';
import ProposalSidebar from './components/ProposalSidebar';
import { ProposalHistorySelectorHandle } from './components/ProposalHistorySelector';
import { ProposalService } from './services/proposalService';
import { ProposalWebSocketService, ProgressUpdate } from './services/proposalWebSocketService';
import { useProposalGenerator } from './hooks/useProposalGenerator';
import { generateProposalFilename } from '../../utils/fileNaming';
import './ProposalGenerator.css';
import { deriveClientName } from './utils/clientName';

const ProposalGenerator: React.FC = () => {
  const { getAccessToken, user } = useAuth();
  const {
    state,
    actions,
    validation
  } = useProposalGenerator();

  const [service, setService] = useState<ProposalService | null>(null);
  const [wsService] = useState<ProposalWebSocketService>(() => new ProposalWebSocketService());
  const [isSidebarOpen, setSidebarOpen] = useState(true);
  const [isDownloading, setIsDownloading] = useState(false);
  const [, setGenerationStartTime] = useState<Date | null>(null);
  const [proposalListRefresh, setProposalListRefresh] = useState(0);
  const historySelectorRef = useRef<ProposalHistorySelectorHandle>(null);
  
  // WebSocket progress tracking
  const [generationSteps, setGenerationSteps] = useState<Array<{
    id: string; 
    label: string; 
    status: 'pending' | 'active' | 'completed'
  }>>([]);
  
  // Force component re-render tracker
  const [, forceUpdate] = useState({});

  // DAG-002 (Round 7 #127) + DAG-003 (Round 6 #105 pattern):
  // diagnostic-only logging. The previous version logged the full
  // step list, raw progress payload, and complete WS response body —
  // all of which carry generated profile content / governed client
  // data. Now only non-sensitive shape (step count, type/progress
  // numbers, profile/failure counts) goes to the console.
  const handleWebSocketStepsUpdate = useCallback((steps: Array<{id: string; label: string; status: 'pending' | 'active' | 'completed'}>) => {
    setGenerationSteps([...steps]);
    forceUpdate({});
  }, []);

  const handleWebSocketProgressUpdate = useCallback((update: ProgressUpdate) => {
    if (update?.type) {
      console.debug(`WS progress type=${update.type} progress=${update.progress ?? 'n/a'}`);
    }
  }, []);

  const handleWebSocketComplete = useCallback((data: any) => {
    const profileCount = data?.profiles?.length ?? 0;
    const failedCount = data?.failed_count ?? 0;
    console.debug(`WS generation complete: profile_count=${profileCount} failed_count=${failedCount}`);

    if (failedCount > 0) {
      // DAG-002: failed_users contains user identifiers — count only.
      console.warn(`${failedCount} profile(s) failed to generate`);
    }

    // Check if we received profiles but they're all empty
    if (data.profiles && data.profiles.length === 0) {
      console.error('Generation completed but no profiles were generated. Check backend logs for errors.');
      // Clear stale state before showing error
      actions.setGeneratedProfiles([]);
      actions.setProposalId(undefined);
      actions.setMetadata(undefined);

      // Show more specific error if we have failure information
      let errorMessage = 'Generation completed but no profiles were created.';
      if (data.failed_count) {
        errorMessage = `All ${data.failed_count} profile(s) failed to generate. `;
        if (data.failed_users && data.failed_users.length > 0) {
          errorMessage += `Failed users: ${data.failed_users.join(', ')}`;
        }
      }

      actions.setError(errorMessage);
      actions.setGenerating(false);
      return;
    }

    if (data.profiles && data.profiles.length > 0) {
      // Map backend profiles to ProfileData format
      const mappedProfiles = data.profiles.map((profile: any) => ({
        id: profile.user_id || profile.id,
        userId: profile.user_id,
        name: profile.display_name || profile.user_id || 'Unknown',
        title: '',  // Will be extracted from markdown if needed
        location: '',  // Will be extracted from markdown if needed
        content: profile.content || '',  // Current content for editing/export
        originalContent: profile.content || '',  // Save original for reverting
        format: profile.format || 'markdown',
        format_type: profile.format_type || data.format_type || state.formatType,
        generated_at: profile.generated_at,
        isDirty: false
      }));
      
      const resolvedClientName = deriveClientName({
        metadataName: data.client_name,
        extractedName: state.clientContext.extractedInfo?.companyName,
        manualInput: state.clientContext.manualInput,
        url: state.clientContext.url
      });

      actions.setGeneratedProfiles(mappedProfiles);
      actions.setProposalId(data.proposal_id);
      actions.setMetadata({
        clientName: resolvedClientName,
        createdAt: new Date(data.created_at),
        updatedAt: new Date(),
        createdBy: user?.localAccountId || user?.homeAccountId || user?.username || 'current_user',
        teamMembers: state.selectedUsers.map(u => u.displayName),
        format: state.formatType
      });
      
      // Save to IndexedDB after successful generation
      setTimeout(async () => {
        try {
          await actions.saveProposal();
          console.log('Proposal auto-saved after generation');

          // Add the new proposal to history list immediately
          const addProposalToHistory = historySelectorRef.current?.addProposal
            || (window as any).__proposalHistoryAddProposal;

          const historyEntry = {
            proposalId: data.proposal_id,
            clientName: resolvedClientName,
            clientSlug: resolvedClientName.toLowerCase().replace(/[^a-z0-9]+/g, '-'),
            createdAt: new Date(data.created_at),
            updatedAt: new Date(),
            format: state.formatType,
            teamMemberCount: state.selectedUsers.length,
            clientUrl: state.clientContext.url
          };

          addProposalToHistory?.(historyEntry);

          // Also trigger refresh as backup
          setProposalListRefresh(Date.now());
        } catch (error) {
          console.error('Failed to auto-save proposal:', error);
        }
      }, 100);

      // Show warning if some profiles failed but others succeeded
      if (data.failed_count && data.failed_count > 0) {
        const warningMessage = `Warning: ${data.failed_count} out of ${data.total_users} ` +
          `profile(s) failed to generate. Successfully generated ${mappedProfiles.length} profile(s).`;
        console.warn(warningMessage, 'Failed users:', data.failed_users);
        // Note: Consider adding a toast notification here for better UX
      }
    }
    actions.setGenerating(false);
  }, [actions, state.selectedUsers, state.formatType, state.clientContext, user, historySelectorRef]);

  const handleWebSocketError = useCallback((error: string) => {
    console.error('WebSocket generation error:', error);
    // Clear stale state when generation fails - matching complete handler logic
    actions.setGeneratedProfiles([]);
    actions.setProposalId(undefined);
    actions.setMetadata(undefined);
    actions.setError(error);
    actions.setGenerating(false);
    setGenerationSteps([]);
  }, [actions]);

  // Event handlers
  const handleTeamSelection = (users: User[]) => {
    actions.setSelectedUsers(users);
  };

  const handleClientContext = (context: ClientContext) => {
    actions.setClientContext(context);
  };

  const handleFormatChange = (format: FormatType) => {
    actions.setFormatType(format);
  };

  // Initialize service with token and preload Graph API token
  useEffect(() => {
    const initService = async () => {
      const token = await getAccessToken();
      if (token) {
        const proposalService = new ProposalService(token);
        setService(proposalService);
        
        // Preload Graph API token for faster user searches
        try {
          const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:9110'}/api/proposal/auth/preload-token`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });
          
          if (response.ok) {
            const data = await response.json();
            console.log('Graph API token preloaded:', data.status);
          }
        } catch (error) {
          console.error('Failed to preload Graph API token:', error);
          // Non-critical error - searches will still work but might be slower on first request
        }
      }
    };
    initService();
  }, [getAccessToken]);

  // Cleanup WebSocket on unmount
  useEffect(() => {
    return () => {
      wsService.disconnect();
    };
  }, [wsService]);

  // WebSocket-based generation with real-time progress
  const handleGenerate = async () => {
    if (!validation.canGenerate()) {
      actions.setError('Please select at least one team member');
      return;
    }

    actions.setGenerating(true);
    actions.setError('');
    actions.setGeneratedProfiles([]); // Clear editor when starting generation
    actions.setExportedFile(undefined, '', undefined); // Clear exported file and PDF viewer
    actions.setShowViewer(false); // Hide viewer when generating new content
    setGenerationStartTime(new Date());
    setSidebarOpen(false); // Hide sidebar when generating
    
    // Initialize generation steps based on whether we need to scrape
    // Don't scrape if we already have scraped content
    const needsScraping = state.clientContext.url &&
                         !state.clientContext.scrapedContent;
    
    const initialSteps: Array<{id: string; label: string; status: 'pending' | 'active' | 'completed'}> = needsScraping ? [
      { id: 'scrape', label: 'Processing client website', status: 'pending' as const },
      { id: 'fetch', label: 'Fetching user profiles', status: 'pending' as const },
      { id: 'generate', label: 'Generating tailored bios', status: 'pending' as const }
    ] : [
      { id: 'fetch', label: 'Fetching user profiles', status: 'pending' as const },
      { id: 'generate', label: 'Generating tailored bios', status: 'pending' as const }
    ];
    
    setGenerationSteps(initialSteps);

    try {
      // Prepare WebSocket request
      // If we have scraped content, send it as client_context instead of URL
      // Create user_info map for display names
      const user_info: Record<string, { display_name: string }> = {};
      state.selectedUsers.forEach(user => {
        user_info[user.id] = { display_name: user.displayName };
      });

      const clientName = deriveClientName({
        metadataName: state.metadata?.clientName,
        extractedName: state.clientContext.extractedInfo?.companyName,
        manualInput: state.clientContext.manualInput,
        url: state.clientContext.url
      });

      const accessToken = await getAccessToken();
      if (!accessToken) {
        actions.setError('Unable to acquire authentication token. Please sign in again.');
        actions.setGenerating(false);
        setGenerationSteps([]);
        return;
      }

      const request = {
        user_ids: state.selectedUsers.map(u => u.id),
        client_url: state.clientContext.url,  // Always send the URL
        client_context: state.clientContext.manualInput,  // Always send the manual input
        scraped_content: state.clientContext.scrapedContent,  // Send cached scraped content
        extracted_info: state.clientContext.extractedInfo,  // Send extracted company info
        format_type: state.formatType,
        client_name: clientName,
        user_info
      };

      // DAG-001 (Round 7 #126): the previous log dumped the full
      // request including client_context, scraped_content,
      // extracted_info, and user_info — governed client and employee
      // identifying data straight into the browser console. Log only
      // the non-sensitive shape: how many users, which format type.
      console.debug(
        `Starting WS generation: users=${request.user_ids.length} format=${request.format_type}`
      );

      // Generate with WebSocket progress updates using stable callbacks
      await wsService.generateWithProgress(
        request,
        accessToken,
        handleWebSocketProgressUpdate,
        handleWebSocketStepsUpdate,
        handleWebSocketComplete,
        handleWebSocketError
      );
    } catch (error) {
      console.error('Generation failed:', error);
      actions.setError(error instanceof Error ? error.message : 'Generation failed');
      actions.setGenerating(false);
      setGenerationSteps([]);
    }
  };

  const handleExport = async (options: ExportOptions) => {
    if (!service || !validation.canExport()) {
      actions.setError('No profiles available for export');
      return;
    }

    // Save proposal before exporting
    try {
      await actions.saveProposal();
    } catch (error) {
      console.error('Failed to save proposal:', error);
      // Continue with export even if save fails
    }

    // Show viewer immediately with loading state
    actions.setShowViewer(true);
    actions.setExporting(true);
    // Clear previous export to show loading state
    actions.setExportedFile(undefined, '', undefined);

    try {
      let profiles = state.editedProfiles.length > 0 ? state.editedProfiles : state.generatedProfiles;

      // If current profile data is provided (from editor), update it in the profiles array
      if (options.currentProfileData && state.currentProfileIndex >= 0 && state.currentProfileIndex < profiles.length) {
        profiles = [...profiles];
        profiles[state.currentProfileIndex] = options.currentProfileData;
      }

      // Ensure all profiles have user_id and format_type fields for backend
      profiles = profiles.map(profile => ({
        ...profile,
        user_id: profile.userId || profile.user_id || profile.id,  // Normalize to user_id for backend
        format_type: profile.format_type || state.generatedFormatType || state.formatType
      }));

      console.log('Profiles being sent for export:', profiles.map(p => ({
        user_id: p.user_id,
        name: p.name
      })));

      // Always use the format that was used during generation, not the currently selected format
      const generationFormat = state.generatedFormatType || state.formatType;

      // Full and web-bio formats use PDF export, others use PowerPoint preview
      const exportFormat = (generationFormat === 'full' || generationFormat === 'web-bio') ? 'pdf' : 'pptx-preview';

      const response = await service.exportProposal(state.proposalId || '', exportFormat, profiles, generationFormat);

      // response is already a Blob from the service
      const blob = response;

      const filename = `proposal.${exportFormat}`;

      actions.setExportedFile(blob, filename, generationFormat);

    } catch (error) {
      console.error('Export failed:', error);
      actions.setError('Export failed');
      // Hide viewer on error
      actions.setShowViewer(false);
    } finally {
      actions.setExporting(false);
    }
  };

  const handleLoadProposal = async (proposalId: string, _clientName: string) => {
    // Load proposal from backend
    actions.loadProposal(proposalId);
  };

  const handleDownload = async () => {
    // Check the actual exported format, not the currently selected format
    const isExportedPdf = state.exportedFormatType === 'full' || state.exportedFormatType === 'web-bio';

    // Set downloading state for PowerPoint downloads (PDFs are instant)
    if (!isExportedPdf) {
      setIsDownloading(true);
    }

    // For PDF exports, download the PDF that's already in the viewer
    if (isExportedPdf) {
      if (state.exportedFile && state.exportedFileName) {
        const url = URL.createObjectURL(state.exportedFile);
        const a = document.createElement('a');
        a.href = url;
        a.download = state.exportedFileName;
        a.click();
        URL.revokeObjectURL(url);
      }
    } else {
      // For PowerPoint exports, download the PowerPoint file
      if (!service || !validation.canExport()) {
        actions.setError('No profiles available for download');
        return;
      }

      try {
        let profiles = state.editedProfiles.length > 0 ? state.editedProfiles : state.generatedProfiles;

        // Use the format that was used for generation/export, not the current selection
        const originalFormatType = state.exportedFormatType || state.generatedFormatType || state.formatType;

        // Ensure all profiles have user_id and format_type fields for backend
        profiles = profiles.map(profile => ({
          ...profile,
          user_id: profile.userId || profile.user_id || profile.id,  // Normalize to user_id for backend
          format_type: profile.format_type || originalFormatType
        }));

        console.log('Profiles being sent for PowerPoint download:', profiles.map(p => ({
          user_id: p.user_id,
          name: p.name
        })));
        const response = await service.exportProposal(state.proposalId || '', 'pptx', profiles, originalFormatType);

        const blob = response;
        const filename = generateProposalFilename({
          clientName: state.clientContext.extractedInfo?.companyName || state.metadata?.clientName || 'Proposal',
          format: 'pptx'
        });

        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      } catch (error) {
        console.error('Download failed:', error);
        actions.setError('Download failed');
      } finally {
        setIsDownloading(false);
      }
    }
  };

  const toggleSidebar = () => {
    setSidebarOpen(!isSidebarOpen);
  };

  // Memoize the file object for DocumentViewer to prevent unnecessary re-renders
  // Only create a new File object when exportedFile or exportedFileName actually changes
  const exportedFileObject = useMemo(() => {
    if (!state.exportedFile) return null;
    return new File([state.exportedFile], state.exportedFileName || 'document.pdf', { type: 'application/pdf' });
  }, [state.exportedFile, state.exportedFileName]);

  if (!service) {
    return (
      <div className="proposal-generator-container">
        <div className="workspace">
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <Typography variant="body1">Loading...</Typography>
          </Box>
        </div>
      </div>
    );
  }

  return (
    <ErrorBoundary>
      <div className="proposal-generator-container">
        {/* Sidebar */}
        <ProposalSidebar
          isOpen={isSidebarOpen}
          onToggle={toggleSidebar}
          selectedUsers={state.selectedUsers}
          onUsersChange={handleTeamSelection}
          clientContext={state.clientContext}
          onClientChange={handleClientContext}
          formatType={state.formatType}
          onFormatChange={handleFormatChange}
          onGenerate={handleGenerate}
          isGenerating={state.isGenerating}
          service={service}
          onLoadProposal={handleLoadProposal}
          onReset={actions.reset}
          currentProposalId={state.proposalId}
          currentClientName={state.metadata?.clientName}
          refreshTrigger={proposalListRefresh}
          resetCounter={state.resetCounter}
          historyRef={historySelectorRef}
        />

        {/* Main Workspace */}
        <div className="workspace">
          {/* Show editor when we have generated profiles */}
          {state.generatedProfiles.length > 0 && (
            <div className={`panels-container ${state.showViewer ? 'two-panel-layout' : ''}`}>
              {/* Profile Editor Panel */}
              <div className="panel editor-panel">
                <ProfileEditor
                  profiles={state.editedProfiles.length > 0 ? state.editedProfiles : state.generatedProfiles}
                  currentIndex={state.currentProfileIndex}
                  formatType={state.formatType}
                  clientName={state.metadata?.clientName || state.clientContext.url}
                  onProfileChange={(index, profile) => actions.updateProfile(index, profile)}
                  onNavigate={actions.setCurrentProfileIndex}
                  onExport={(currentProfileData) => handleExport({ format: 'pdf', currentProfileData })}  // Always preview as PDF
                />
              </div>

              {/* Viewer Panel (slides in from right when showViewer is true) */}
              {state.showViewer && (
                <div className="panel viewer-panel">
                  <div className="panel-header">
                    <div className="panel-title">
                      <span>Preview (PDF)</span>
                    </div>
                    <div className="panel-controls">
                      <button
                        className="download-btn"
                        onClick={handleDownload}
                        disabled={isDownloading}
                        title={(state.exportedFormatType === 'full' || state.exportedFormatType === 'web-bio') ? 'Download PDF' : 'Download PowerPoint'}
                      >
                        {isDownloading ? (
                          <>
                            <div className="spinner" style={{ marginRight: '8px', width: '16px', height: '16px', borderWidth: '2px' }}></div>
                            Preparing Download...
                          </>
                        ) : (
                          <>
                            <span className="material-icons">download</span>
                            {(state.exportedFormatType === 'full' || state.exportedFormatType === 'web-bio') ? 'Download PDF' : 'Download PowerPoint'}
                          </>
                        )}
                      </button>
                      <button 
                        className="control-btn"
                        onClick={() => actions.setShowViewer(false)}
                        title="Close viewer"
                      >
                        <span className="material-icons">close</span>
                      </button>
                    </div>
                  </div>
                  <div className="panel-content">
                    {state.isExporting || !state.exportedFile ? (
                      <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: '600px',
                        color: 'rgba(255,255,255,0.8)'
                      }}>
                        <CircularProgress size={60} sx={{ mb: 3 }} />
                        <Typography variant="h6">Generating Export...</Typography>
                        <Typography variant="body2" sx={{ mt: 1, opacity: 0.7 }}>
                          Creating PDF preview
                        </Typography>
                      </div>
                    ) : exportedFileObject ? (
                      <DocumentViewer
                        file={exportedFileObject}
                        fileType="pdf"
                        height={600}
                        showToolbar={true}
                        onError={(error) => console.error('PDF viewer error:', error)}
                      />
                    ) : null}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Empty state when no profiles */}
          {state.generatedProfiles.length === 0 && !state.isGenerating && (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              textAlign: 'center',
              padding: '48px'
            }}>
              <span className="material-icons" style={{
                fontSize: '96px',
                color: 'rgba(255,255,255,0.3)',
                marginBottom: '24px'
              }}>group</span>
              <h2 style={{
                color: 'rgba(255,255,255,0.8)',
                fontSize: '24px',
                fontWeight: '500',
                marginBottom: '16px'
              }}>Welcome to Bio Generator</h2>
              <p style={{
                color: 'rgba(255,255,255,0.6)',
                fontSize: '16px',
                maxWidth: '400px',
                lineHeight: '1.6'
              }}>Select team members and configure client context in the sidebar to generate tailored bios</p>
            </div>
          )}
        </div>

        {/* Generation Progress Modal */}
        <Modal
          open={state.isGenerating}
          onClose={() => {}}
          disableEscapeKeyDown
          aria-labelledby="generation-progress-modal"
        >
          <Box 
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: 500,
              bgcolor: 'background.paper',
              borderRadius: 2,
              boxShadow: 24,
            }}
          >
            {generationSteps.length > 0 ? (
              <GenerationProgress 
                steps={generationSteps}
                currentStep={generationSteps.find(s => s.status === 'active')?.id}
              />
            ) : (
              <Box sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Generating Bios
                </Typography>
                <Box sx={{ mt: 2 }}>
                  <LinearProgress />
                  <Typography variant="body2" sx={{ mt: 2 }} color="text.secondary">
                    Initializing generation...
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>
        </Modal>
      </div>
    </ErrorBoundary>
  );
};

export default ProposalGenerator;
